import React from 'react';
import { Typography, Box, Link, Divider } from '@mui/material';
import SEOHead from './components/SEOHead';
import { Card } from './components/ui';
import { acme } from './theme/acmeTheme';
import {
  legalPageOuterSx,
  legalPageCardSx,
  legalPageTitleSx,
  legalPageUpdatedSx,
  legalSectionTitleSx,
  legalBodySx,
  legalFooterSx,
} from './components/legal/legalDocumentStyles';

const LEGAL_LAST_UPDATED = 'June 19, 2026';

const PrivacyPolicy = () => {
  return (
    <>
      <SEOHead pageName="privacy-policy" />
      <Box sx={legalPageOuterSx}>
      <Card elevation={0} sx={legalPageCardSx}>
        <Typography component="h1" sx={legalPageTitleSx}>
          Privacy Policy
        </Typography>
        <Typography sx={legalPageUpdatedSx}>
          Last updated: {LEGAL_LAST_UPDATED}
        </Typography>
        <Divider sx={{ my: 3, borderColor: acme.colors.border }} />

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            1. Company Information and Data Controller
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>1.1 Service Provider:</strong> This Privacy Policy describes how <strong>Atikut OÜ</strong>, a private limited company (Estonian <em>osaühing</em>, registry code <strong>17460898</strong>, registered on 17 March 2026) ("we", "us", "our", "Company"), collects, uses, stores, and protects your personal data when you use our Mailsball email and SMS marketing platform ("Service"). The platform is owned and operated by Atikut OÜ. The management board of Atikut OÜ includes Oleksandr Khrustalov; the sole shareholder and ultimate beneficial owner is Oleksandr Khrustalov.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>1.2 Data controller:</strong> For personal data related to your Mailsball account and your use of the Service, the data controller is <strong>Atikut OÜ</strong> (registry code 17460898, Estonia), unless stated otherwise in this policy.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>1.3 Data Protection Officer (DPO):</strong> For data protection matters, please contact our Data Protection Officer at dpo@mailsball.com or at the address provided in Section 17.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>1.4 Legal Basis:</strong> This Privacy Policy is established in accordance with the General Data Protection Regulation (GDPR), Estonian Data Protection Act, and other applicable data protection laws.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>1.5 Scope:</strong> By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy. This policy applies to all users of our platform, regardless of location.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            2. Information We Collect
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.1 Account Registration Data:</strong> When you create an account, we collect personal information including your full name, email address, password (encrypted), company name, job title, billing address, phone number, and other contact details.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.2 Payment Information:</strong> We collect billing information necessary for payment processing, including credit card details (processed by secure third-party payment processors), billing address, and transaction history. We do not store complete payment card information on our servers.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.3 Campaign and Content Data:</strong> We collect and store all content you create through our platform, including email campaigns, SMS campaigns and message bodies, templates, images, text, HTML code, sender labels, and any other materials you upload or create.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.4 Contact Lists and Recipient Data:</strong> When you upload contact lists, we collect and process information about your email and SMS recipients, including their names, email addresses, phone numbers, opt-in/opt-out status, and any additional fields you provide. You act as the data controller for this information; Atikut OÜ acts as your data processor when we store or transmit it solely to provide the Service.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.5 SMS and Telecommunications Data:</strong> When you use SMS features, we process mobile phone numbers (E.164 format), sender identity/label information, carrier delivery receipts (sent, delivered, failed), SMS segment counts, opt-out replies (e.g. STOP), clickable unsubscribe events, and related metadata required to deliver messages and comply with telecommunications regulations.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.6 Usage and Analytics Data:</strong> We automatically collect information about how you use our platform, including login times, feature usage, email and SMS campaign performance metrics, click-through rates, open rates, delivery rates, and interaction patterns with our interface.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.7 Technical Information:</strong> We collect technical data including your IP address, browser type and version, operating system, device information, screen resolution, referral URLs, and other technical specifications.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.8 Communication Data:</strong> We collect and store records of communications between you and our support team, including support tickets, chat logs, and email correspondence.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.9 Social Media Integration:</strong> If you use social media login features, we may receive profile information from these platforms in accordance with their privacy policies and your authorization settings.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>2.10 SMS Sender Verification Data:</strong> If you register a sender label or phone number for SMS, we may collect company legal name, billing address, contact email, and other KYC information required by telecommunications providers or regulators before enabling sending.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            3. Legal Basis for Processing
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            Under GDPR, we process your personal data based on the following legal grounds:
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>3.1 Contract Performance:</strong> Processing necessary to provide our services and fulfill our contractual obligations to you.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>3.2 Legitimate Interests:</strong> Processing for our legitimate business interests, including service improvement, security, fraud prevention, and business analytics, balanced against your rights and freedoms.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>3.3 Legal Compliance:</strong> Processing required to comply with legal obligations, including tax laws, accounting requirements, and regulatory compliance.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>3.4 Consent:</strong> Processing based on your explicit consent, which you may withdraw at any time for marketing communications and optional features.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>3.5 Vital Interests:</strong> Processing necessary to protect your vital interests or those of another person in emergency situations.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            4. How We Use Your Information
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>4.1 Service Provision:</strong> To create and maintain your account, process payments, deliver email and SMS campaigns, manage opt-outs, provide customer support, and enable all platform features and functionalities.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>4.2 Communication:</strong> To send you important account notifications, service updates, security alerts, billing information, and transactional emails. We may also send marketing communications if you have opted in.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>4.3 Service Improvement:</strong> To analyze usage patterns, conduct research, develop new features, improve existing functionality, and enhance user experience.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>4.4 Security and Fraud Prevention:</strong> To detect, prevent, and investigate security breaches, fraudulent activities, and unauthorized access to our systems.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>4.5 Legal Compliance:</strong> To comply with applicable laws, regulations, legal processes, and governmental requests.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>4.6 Business Operations:</strong> To conduct business analysis, reporting, quality assurance, and other internal business operations.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            5. Cookies and Tracking Technologies
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>5.1 Cookie Policy:</strong> We use cookies and similar tracking technologies to enhance your experience, remember your preferences, analyze usage patterns, and provide personalized content. Our cookie usage complies with GDPR requirements.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>5.2 Types of Cookies:</strong>
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>• Strictly Necessary Cookies:</strong> Essential for website functionality, security, and your ability to use our services. These cannot be disabled.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>• Performance Cookies:</strong> Collect information about how you use our website to help us improve its performance and user experience.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>• Functional Cookies:</strong> Remember your preferences and settings to provide you with a personalized experience.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>• Marketing Cookies:</strong> Used to deliver relevant advertisements and track the effectiveness of our marketing campaigns.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>5.3 Cookie Consent:</strong> We obtain your explicit consent for non-essential cookies through our cookie consent banner. You can manage your cookie preferences through your browser settings or our cookie preference center.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>5.4 Third-Party Analytics:</strong> We use third-party analytics services, including Google Analytics, to understand user behavior. These services may use cookies and similar technologies subject to their own privacy policies.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            6. Email and SMS Tracking, Delivery Analytics, and Opt-Outs
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>6.1 Email Tracking:</strong> Our platform provides tracking features to monitor email campaign performance, including open rates, click-through rates, bounce rates, and recipient engagement metrics.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>6.2 Email Tracking Methods:</strong> We use tracking pixels, unique identifiers, and link tracking to collect campaign performance data. This information helps our users optimize their email marketing effectiveness.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>6.3 SMS Delivery Reporting:</strong> For SMS campaigns, we record delivery status reports received from telecommunications providers (e.g. sent, delivered, failed), message segment counts, and timestamps. We do not guarantee that all delivery events are reported by carriers.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>6.4 SMS Opt-Out Processing:</strong> We automatically process SMS opt-out requests submitted via reply keywords (such as STOP) or clickable unsubscribe links. Opt-out status is stored and applied to prevent further promotional messages to that recipient through your account, subject to legal and technical limitations.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>6.5 Recipient Rights:</strong> Email and SMS recipients have the right to object to marketing and to opt out. Email recipients may disable image loading to reduce tracking. SMS recipients may use STOP replies or unsubscribe links provided in messages.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>6.6 Data Controller Responsibility:</strong> Users of our platform are responsible for informing their recipients about processing activities, obtaining valid consent or another lawful basis where required, honouring opt-out requests promptly, and maintaining proof of consent for SMS and email marketing as required by applicable laws (including GDPR, ePrivacy rules, CAN-SPAM, CASL, TCPA/CTIA guidelines, and local telecommunications regulations).
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            7. Data Sharing and Disclosure
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.1 Service Providers:</strong> We share your information with trusted third-party service providers who assist us in operating our platform, including hosting providers, payment processors (such as Stripe), email delivery services, SMS aggregators and telecommunications providers (such as BulkGate), and analytics providers. These providers process data only on our instructions and are bound by contractual confidentiality and data protection obligations where applicable.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.2 Payment Processing:</strong> Payment information is processed by PCI-compliant third-party payment processors. We do not store complete credit card information on our servers.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.3 Legal Requirements:</strong> We may disclose your information if required by law, court order, or governmental request, or to protect our rights, property, or safety, or that of others.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.4 Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity. We will notify you of any such transfer and any changes to this Privacy Policy.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.5 Aggregated Data:</strong> We may share aggregated, anonymized data that does not identify individual users for business purposes, research, or analytics.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.6 User Consent:</strong> We will obtain your explicit consent before sharing your personal information for purposes not covered by this Privacy Policy.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>7.7 SMS Sub-Processors:</strong> To deliver SMS messages, recipient phone numbers, message content, and sender identifiers may be transmitted to our SMS delivery partners and underlying mobile network operators. Such transfers are necessary to perform the Service and are subject to appropriate safeguards and data processing agreements.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            8. International Data Transfers
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>8.1 Cross-Border Processing:</strong> We may transfer your personal data to countries outside the European Economic Area (EEA), including the United States, for service provision and support operations.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>8.2 Safeguards:</strong> For transfers to countries without adequate data protection levels, we implement appropriate safeguards including Standard Contractual Clauses (SCCs) approved by the European Commission.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>8.3 Data Processing Agreements:</strong> All third-party service providers who process your data are required to enter into data processing agreements that ensure adequate protection of your personal information.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>8.4 Your Rights:</strong> You have the right to obtain information about these transfers and request copies of the safeguards we have implemented.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            9. Data Security and Protection
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>9.1 Security Measures:</strong> We implement comprehensive technical and organizational security measures to protect your personal data, including encryption at rest and in transit, access controls, regular security audits, and employee training.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>9.2 Access Controls:</strong> We maintain strict access controls ensuring that only authorized personnel can access your personal data on a need-to-know basis.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>9.3 Data Breach Response:</strong> In the event of a data breach, we will notify relevant authorities within 72 hours and affected individuals without undue delay, as required by GDPR.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>9.4 Regular Audits:</strong> We conduct regular security assessments and penetration testing to identify and address potential vulnerabilities.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>9.5 Employee Training:</strong> All employees with access to personal data receive regular training on data protection principles and security best practices.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            10. Data Retention and Deletion
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>10.1 Retention Periods:</strong> We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, and resolve disputes.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>10.2 Account Data:</strong> Account information is retained for the duration of your account and up to 7 years after account closure for legal and tax compliance purposes.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>10.3 Campaign Data:</strong> Email and SMS campaigns and associated delivery, billing, and opt-out records are retained for up to 3 years after creation for analytics, dispute resolution, and legal compliance purposes, unless a longer period is required by law.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>10.4 Automatic Deletion:</strong> We implement automated deletion processes to ensure data is removed according to our retention schedules.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>10.5 User-Requested Deletion:</strong> You may request deletion of your personal data at any time, subject to legal retention requirements and our need to retain records relating to billing, fraud prevention, or compliance investigations.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>10.6 SMS Opt-Out Records:</strong> Records of SMS opt-outs are retained as long as necessary to honour unsubscribe requests and demonstrate compliance, even after account closure where legally required.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            11. Your Rights Under GDPR
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            As a data subject, you have the following rights under GDPR:
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.1 Right of Access:</strong> You have the right to obtain confirmation of whether we process your personal data and access to that data, along with information about the processing.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.2 Right to Rectification:</strong> You have the right to request correction of inaccurate personal data and completion of incomplete data.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.3 Right to Erasure:</strong> You have the right to request deletion of your personal data under certain circumstances, including when the data is no longer necessary for the original purpose.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.4 Right to Restrict Processing:</strong> You have the right to request restriction of processing under certain circumstances, such as when you contest the accuracy of the data.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.5 Right to Data Portability:</strong> You have the right to receive your personal data in a structured, commonly used, and machine-readable format and transmit it to another controller.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.6 Right to Object:</strong> You have the right to object to processing based on legitimate interests or for direct marketing purposes.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.7 Right to Withdraw Consent:</strong> Where processing is based on consent, you have the right to withdraw consent at any time.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.8 Right to Lodge a Complaint:</strong> You have the right to lodge a complaint with the Estonian Data Protection Inspectorate or your local supervisory authority.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>11.9 Exercising Your Rights:</strong> To exercise any of these rights, please contact our Data Protection Officer at dpo@mailsball.com. We will respond to your request within 30 days.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            12. Automated Decision-Making and Profiling
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>12.1 Automated Processing:</strong> We may use automated processing to analyze campaign performance, detect fraud, and improve our services. This processing does not involve automated decision-making that produces legal effects.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>12.2 Profiling:</strong> We may create profiles based on your usage patterns to provide personalized recommendations and improve your experience. You have the right to object to such profiling.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>12.3 No Automated Decision-Making:</strong> We do not make automated decisions that significantly affect you without human involvement.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            13. Third-Party Services and Links
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>13.1 Third-Party Integrations:</strong> Our platform integrates with third-party services to enhance functionality. These integrations are governed by the privacy policies of the respective third parties.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>13.2 External Links:</strong> Our platform may contain links to third-party websites. We are not responsible for the privacy practices of these external sites.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>13.3 Social Media:</strong> Our platform may include social media features. Your interactions with these features are governed by the privacy policies of the social media platforms.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            14. Children's Privacy
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>14.1 Age Restriction:</strong> Our services are not intended for children under 16 years of age. We do not knowingly collect personal information from children under 16.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>14.2 Parental Rights:</strong> If you are a parent or guardian and believe your child has provided personal information to us, please contact us immediately, and we will delete such information.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>14.3 Verification:</strong> We may request age verification to ensure compliance with this policy.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            15. Changes to This Privacy Policy
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>15.1 Updates:</strong> We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or business needs.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>15.2 Notification:</strong> We will notify you of material changes via email or prominent notice on our website at least 30 days before the changes take effect.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>15.3 Consent:</strong> Continued use of our services after changes take effect constitutes acceptance of the updated Privacy Policy.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>15.4 Version Control:</strong> We maintain previous versions of this Privacy Policy for reference and compliance purposes.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            16. Platform Role, Customer Responsibilities, and Limitations
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>16.1 Processor Role:</strong> For personal data of your email and SMS recipients that you upload or cause to be processed through the Service, you are the data controller and Atikut OÜ acts as your data processor, processing such data only on your documented instructions as set out in these Terms and this Privacy Policy.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>16.2 Your Warranties:</strong> You represent that you have a valid legal basis (including consent where required) to process recipient data, that your lists are not sourced unlawfully, and that your email and SMS content complies with applicable law.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>16.3 No Legal Advice:</strong> We do not provide legal advice. You are solely responsible for determining whether your use of email or SMS marketing complies with laws in each jurisdiction where you send messages.
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>16.4 Compliance Enforcement:</strong> We may monitor usage for abuse, suspend or terminate accounts, block campaigns, or report unlawful activity to authorities where we reasonably believe violations have occurred, to protect recipients, carriers, and our platform.
          </Typography>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Typography component="h2" sx={legalSectionTitleSx}>
            17. Contact Information
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>Data controller:</strong><br />
            Atikut OÜ (Osaühing / private limited company)<br />
            Registry code: 17460898 (Commercial Register of Estonia)<br />
            Address: Estonia<br />
            Email: info@mailsball.com<br />
            Phone: +372 5359 1740
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>Contact person:</strong><br />
            Oleksandr Khrustalov (management board)<br />
            Email: aleksandr@mailsball.com
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>Data Protection Officer:</strong><br />
            Email: dpo@mailsball.com<br />
            Address: Atikut OÜ, Estonia
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            <strong>Supervisory Authority:</strong><br />
            Estonian Data Protection Inspectorate<br />
            Website: <Link href="https://www.aki.ee" target="_blank" rel="noopener noreferrer">www.aki.ee</Link>
          </Typography>
          <Typography sx={legalBodySx} paragraph>
            For privacy-related inquiries, data subject requests, or complaints, please contact our Data Protection Officer. We are committed to addressing your concerns and resolving any issues promptly and in compliance with applicable data protection laws.
          </Typography>
        </Box>

        <Divider sx={{ mt: 5, mb: 3, borderColor: acme.colors.border }} />
        <Typography sx={legalFooterSx}>
          © {new Date().getFullYear()} Atikut OÜ. All rights reserved.
        </Typography>
      </Card>
    </Box>
    </>
  );
};

export default PrivacyPolicy;
